import React, { useState, useEffect } from 'react';
import { X, Package, Truck, CheckCircle, MapPin, Copy, ExternalLink, Calendar, Star, Loader2 } from 'lucide-react';
import { Order } from '../../types';
import { db } from '../../services/firebase';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';

interface OrderTrackingModalProps {
  isOpen: boolean;
  onClose: () => void;
  order: Order | null;
}

export const OrderTrackingModal: React.FC<OrderTrackingModalProps> = ({ isOpen, onClose, order }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [localConfirmed, setLocalConfirmed] = useState(false);
  const [localRated, setLocalRated] = useState(false);
  const [rating, setRating] = useState(0);
  const [hoveredStar, setHoveredStar] = useState(0);

  // Reset local state when a new order is opened
  useEffect(() => {
    if (order) {
      const o = order as any; // Cast to access custom fields safely
      setLocalConfirmed(o.userConfirmed || false);
      setLocalRated(!!o.rating);
      setRating(o.rating || 0);
    }
  }, [order]);

  if (!isOpen || !order) return null;

  // 1. DYNAMIC PROVIDER LOGIC
  const getProviderInfo = (method: string) => {
    const m = method?.toLowerCase() || '';
    if (m.includes('courier guy')) return { name: 'The Courier Guy', color: 'text-orange-500', border: 'border-orange-500/50' };
    if (m.includes('uber')) return { name: 'Uber Connect', color: 'text-green-500', border: 'border-green-500/50' };
    if (m.includes('aramex')) return { name: 'Aramex', color: 'text-red-500', border: 'border-red-500/50' };
    if (m.includes('pargo')) return { name: 'Pargo', color: 'text-yellow-500', border: 'border-yellow-500/50' };
    return { name: method || 'Standard Courier', color: 'text-white', border: 'border-white/50' };
  };

  const provider = getProviderInfo(order.deliveryMethod);
  const currentStatus = order.status || 'pending';
  
  // 2. TIMELINE STEPS
  const steps = [
    { 
      label: 'Order Placed', 
      date: order.createdAt ? new Date((order.createdAt as any).seconds * 1000).toLocaleDateString() : 'Just now', 
      completed: true, 
      icon: CheckCircle 
    },
    { 
      label: 'Processing', 
      date: currentStatus === 'pending' ? 'In Progress' : 'Done', 
      completed: ['processing', 'shipped', 'delivered'].includes(currentStatus), 
      icon: Package 
    },
    { 
      label: `Handed to ${provider.name}`, 
      date: currentStatus === 'shipped' ? 'On the way' : 'Pending', 
      completed: ['shipped', 'delivered'].includes(currentStatus), 
      icon: Truck 
    },
    { 
      label: 'Delivered', 
      date: currentStatus === 'delivered' ? 'Arrived' : 'Est. 3-5 Days', 
      completed: currentStatus === 'delivered', 
      icon: MapPin 
    },
  ];

  // 🟢 3. CONFIRMATION & RATING LOGIC
  const handleConfirmDelivery = async () => {
      setIsSubmitting(true);
      try {
          await updateDoc(doc(db, 'orders', order.id), {
              userConfirmed: true,
              confirmedAt: serverTimestamp()
          });
          setLocalConfirmed(true);
      } catch (error) {
          console.error("Error confirming delivery:", error);
          alert("Failed to confirm delivery. Please try again.");
      } finally {
          setIsSubmitting(false);
      }
  };

  const handleSubmitRating = async () => {
      if (rating === 0) return;
      setIsSubmitting(true);
      try {
          await updateDoc(doc(db, 'orders', order.id), {
              rating: rating,
              ratedAt: serverTimestamp()
          });
          setLocalRated(true);
      } catch (error) {
          console.error("Error submitting rating:", error);
          alert("Failed to submit rating. Please try again.");
      } finally {
          setIsSubmitting(false);
      }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-0 lg:p-6 pointer-events-none">
        
        {/* BACKDROP */}
        <div 
          className="absolute inset-0 bg-black/90 backdrop-blur-sm pointer-events-auto animate-in fade-in duration-300" 
          onClick={onClose}
        />

        {/* MODAL CONTAINER */}
        <div className="relative w-full h-full lg:max-w-5xl lg:h-[80vh] bg-black lg:border border-zinc-800 lg:rounded-none flex flex-col pointer-events-auto animate-in slide-in-from-bottom lg:zoom-in-95 duration-300 shadow-2xl overflow-hidden mt-auto lg:mt-0">
            
            {/* MOBILE HEADER */}
            <div className="lg:hidden p-6 border-b border-zinc-800 flex justify-between items-center bg-black sticky top-0 z-10 shrink-0">
              <div>
                <h2 className="text-xl font-black text-white italic uppercase tracking-tighter">Tracking Details</h2>
                <div className="flex items-center gap-2 mt-1">
                   <span className="text-zinc-500 text-[10px] font-mono uppercase">#{order.orderNumber}</span>
                </div>
              </div>
              <button onClick={onClose} className="w-10 h-10 bg-zinc-900 rounded-full flex items-center justify-center text-white hover:bg-white hover:text-black transition-colors">
                <X size={20} />
              </button>
            </div>

            {/* SPLIT LAYOUT */}
            <div className="flex-1 overflow-y-auto lg:overflow-hidden flex flex-col lg:flex-row pb-safe">
                
                {/* LEFT PANEL: DETAILS & ITEMS */}
                <div className="w-full lg:w-1/2 flex flex-col lg:h-full lg:overflow-y-auto border-b lg:border-b-0 lg:border-r border-zinc-800 bg-zinc-950">
                    
                    <div className="hidden lg:block p-8 border-b border-zinc-800 bg-black sticky top-0 z-10 shrink-0">
                        <h2 className="text-3xl font-black text-white italic uppercase tracking-tighter">Tracking</h2>
                        <p className="text-zinc-500 font-mono text-xs mt-2 uppercase tracking-widest">Order #{order.orderNumber}</p>
                    </div>

                    <div className="p-6 lg:p-8 space-y-8">
                        {/* Carrier Card */}
                        <div className={`bg-black rounded-2xl lg:rounded-none p-6 border ${provider.border} relative overflow-hidden group`}>
                            <div className="absolute top-6 right-6 opacity-20 group-hover:opacity-100 transition-opacity">
                                <Truck size={40} className={provider.color} strokeWidth={1} />
                            </div>
                            
                            <p className="text-zinc-500 text-[10px] font-black uppercase tracking-widest mb-2">Carrier Info</p>
                            <h3 className="text-2xl lg:text-3xl font-black text-white mb-6 uppercase tracking-tight">{provider.name}</h3>
                            
                            <div className="flex items-center justify-between bg-zinc-900 border border-zinc-800 p-3 lg:p-4 rounded-xl lg:rounded-none">
                                <span className="text-sm font-mono text-white tracking-widest">
                                    {order.id.substring(0, 12).toUpperCase()}
                                </span>
                                <button className="text-zinc-400 hover:text-white transition-colors flex items-center gap-2 text-[10px] font-black uppercase tracking-widest" onClick={() => alert("Copied!")}>
                                    <Copy size={14} /> Copy
                                </button>
                            </div>
                        </div>

                        {/* Order Items Preview */}
                        <div>
                            <h4 className="text-zinc-500 font-bold text-xs uppercase tracking-widest mb-4">Package Contents</h4>
                            <div className="grid grid-cols-2 gap-4">
                                {order.items?.map((item: any, i: number) => (
                                    <div key={i} className="bg-black border border-zinc-800 rounded-xl lg:rounded-none p-3 flex items-center gap-4">
                                        <div className="w-16 h-16 bg-zinc-900 rounded-lg lg:rounded-none overflow-hidden shrink-0 relative">
                                            <img src={item.image || item.imageUrl || (item.images && item.images[0])} className="w-full h-full object-cover grayscale" alt={item.name} />
                                            <div className="absolute top-0 right-0 bg-white text-black text-[10px] font-black w-5 h-5 flex items-center justify-center lg:rounded-none rounded-bl-lg">
                                                {item.quantity}
                                            </div>
                                        </div>
                                        <div className="min-w-0">
                                            <p className="text-xs font-bold text-white truncate uppercase tracking-tight">{item.name}</p>
                                            <p className="text-[10px] text-zinc-500 uppercase tracking-widest mt-1">{item.brand || item.businessName}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* RIGHT PANEL: TIMELINE & ACTIONS */}
                <div className="w-full lg:w-1/2 flex flex-col lg:h-full bg-black relative">
                    
                    <button onClick={onClose} className="hidden lg:flex absolute top-6 right-6 z-50 p-3 bg-zinc-900 border border-zinc-800 text-zinc-500 hover:text-black hover:bg-white rounded-full transition-colors shadow-xl">
                        <X size={20} />
                    </button>

                    <div className="flex-1 p-6 lg:p-12 lg:overflow-y-auto">
                        <h3 className="text-zinc-500 font-bold text-xs uppercase tracking-widest mb-10 hidden lg:block">Delivery Status</h3>
                        
                        <div className="relative pl-4">
                            <div className="absolute left-[31px] top-6 bottom-12 w-[2px] bg-zinc-800"></div>

                            {steps.map((step, index) => {
                                const isCurrent = (currentStatus === 'pending' && index === 1) || (currentStatus === 'shipped' && index === 2) || (currentStatus === 'delivered' && index === 3);
                                const iconColor = step.completed ? 'bg-white text-black ring-4 ring-black' : 'bg-black text-zinc-600 border-2 border-zinc-800 ring-4 ring-black';
                                const textColor = step.completed ? 'text-white' : 'text-zinc-600';

                                return (
                                    <div key={index} className="flex gap-8 relative mb-12 last:mb-0 group">
                                        <div className={`w-10 h-10 rounded-full lg:rounded-none flex items-center justify-center z-10 shrink-0 transition-all duration-500 ${iconColor}`}>
                                            {step.completed ? <CheckCircle size={20} /> : <div className={`w-2 h-2 rounded-full ${isCurrent ? 'bg-white animate-pulse' : 'bg-current'}`} />}
                                        </div>
                                        <div className="flex-1 pt-1.5">
                                            <h4 className={`font-black text-sm lg:text-base uppercase tracking-widest transition-colors ${textColor}`}>{step.label}</h4>
                                            <p className="text-[10px] lg:text-xs text-zinc-500 flex items-center gap-2 mt-2 font-mono uppercase tracking-widest">
                                                <Calendar size={14} /> {step.date}
                                            </p>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* 🟢 DYNAMIC FOOTER: Tracking vs. Confirmation vs. Rating */}
                    <div className="p-6 lg:p-8 border-t border-zinc-800 bg-black shrink-0">
                        {currentStatus !== 'delivered' ? (
                            
                            // SMART TRACKING BUTTON (Only shows if a link exists)
                            (order as any).trackingUrl ? (
                                <button 
                                    onClick={() => window.open((order as any).trackingUrl, '_blank')}
                                    className="w-full py-5 lg:py-6 bg-white text-black font-black text-xs lg:text-sm uppercase tracking-[0.2em] rounded-xl lg:rounded-none flex items-center justify-center gap-3 hover:bg-zinc-200 transition-colors shadow-[0_0_20px_rgba(255,255,255,0.1)]"
                                >
                                    Track on Website <ExternalLink size={16} />
                                </button>
                            ) : (
                                <div className="w-full py-5 lg:py-6 bg-zinc-950 border border-zinc-800 text-zinc-500 font-black text-[10px] lg:text-xs uppercase tracking-[0.2em] rounded-xl lg:rounded-none flex items-center justify-center text-center">
                                    {currentStatus === 'shipped' ? 'Direct Brand Delivery (No Link)' : 'Awaiting Tracking Link'}
                                </div>
                            )

                        ) : !localConfirmed ? (
                            // CONFIRMATION UI (Delivered, but user hasn't clicked confirm)
                            <div className="text-center animate-in slide-in-from-bottom-4 duration-500">
                                <h4 className="text-white font-black text-lg uppercase tracking-widest mb-2">Package Arrived?</h4>
                                <p className="text-zinc-500 font-mono text-[10px] uppercase tracking-widest mb-6 px-4">
                                    Please confirm you have received your items to finalize this transaction.
                                </p>
                                <button 
                                    onClick={handleConfirmDelivery} 
                                    disabled={isSubmitting} 
                                    className="w-full py-5 lg:py-6 bg-green-500 text-black font-black text-xs lg:text-sm uppercase tracking-[0.2em] rounded-xl lg:rounded-none flex items-center justify-center gap-3 hover:bg-green-400 transition-colors disabled:opacity-50 shadow-[0_0_20px_rgba(34,197,94,0.2)]"
                                >
                                    {isSubmitting ? <Loader2 size={20} className="animate-spin" /> : <>Confirm Receipt <CheckCircle size={18} /></>}
                                </button>
                            </div>
                        ) : !localRated ? (
                            // RATING UI (Confirmed, now asking for stars)
                            <div className="text-center animate-in slide-in-from-right duration-500">
                                <h4 className="text-white font-black text-lg uppercase tracking-widest mb-2">Rate Your Experience</h4>
                                <p className="text-zinc-500 font-mono text-[10px] uppercase tracking-widest mb-6">
                                    How was the product and delivery?
                                </p>
                                <div className="flex justify-center gap-2 mb-6">
                                    {[1, 2, 3, 4, 5].map((star) => (
                                        <button 
                                            key={star}
                                            type="button"
                                            onMouseEnter={() => setHoveredStar(star)}
                                            onMouseLeave={() => setHoveredStar(0)}
                                            onClick={() => setRating(star)}
                                            className="p-2 transition-transform hover:scale-110 active:scale-95"
                                        >
                                            <Star 
                                                size={32} 
                                                strokeWidth={1.5}
                                                className={`transition-colors duration-200 ${
                                                    (hoveredStar || rating) >= star 
                                                    ? 'text-yellow-500 fill-yellow-500' 
                                                    : 'text-zinc-700'
                                                }`} 
                                            />
                                        </button>
                                    ))}
                                </div>
                                <button 
                                    onClick={handleSubmitRating} 
                                    disabled={isSubmitting || rating === 0} 
                                    className="w-full py-5 bg-white text-black font-black text-xs uppercase tracking-[0.2em] rounded-xl lg:rounded-none flex items-center justify-center gap-3 hover:bg-zinc-200 transition-colors disabled:opacity-50"
                                >
                                    {isSubmitting ? <Loader2 size={20} className="animate-spin" /> : 'Submit Rating'}
                                </button>
                            </div>
                        ) : (
                            // COMPLETED UI
                            <div className="text-center p-4 border border-zinc-800 rounded-xl lg:rounded-none bg-zinc-950 animate-in fade-in duration-500">
                                <CheckCircle size={32} className="text-green-500 mx-auto mb-3" />
                                <h4 className="text-white font-black uppercase tracking-widest text-sm mb-1">Transaction Complete</h4>
                                <div className="flex justify-center items-center gap-1 text-yellow-500 mb-2">
                                    {[...Array(rating)].map((_, i) => <Star key={i} size={12} fill="currentColor" />)}
                                </div>
                                <p className="text-zinc-500 font-mono text-[10px] uppercase tracking-widest">
                                    Thank you for your feedback.
                                </p>
                            </div>
                        )}
                    </div>

                </div>
            </div>
        </div>
    </div>
  );
};